import { useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { App, Table, Typography } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import Permission from '@/components/permission';
import { registrationChannelListApi, type RegistrationChannelItem } from '@/services/registration-channels';

const PERMISSION = 'Users_Registration-Channels';

const PageRegistrationChannels: React.FC = () => {
    /**
     * Hooks
     */
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    /**
     * State
     */
    const [channels, setChannels] = useState<RegistrationChannelItem[]>([]);

    /**
     * Requests
     */
    const { loading } = useRequest(registrationChannelListApi, {
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg || formatMessage({ id: 'p.urc.msg.loadFailed' }));
            }
            setChannels(data ?? []);
        },
        onError: () => {
            message.error(formatMessage({ id: 'p.urc.msg.loadFailed' }));
        },
    });

    /**
     * Columns
     */
    const columns = [
        {
            title: formatMessage({ id: 'p.urc.columns.name' }),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: formatMessage({ id: 'p.urc.columns.code' }),
            dataIndex: 'code',
            key: 'code',
            render: (code: string) => (
                <Typography.Text copyable={{ text: code }} code>
                    {code}
                </Typography.Text>
            ),
        },
        {
            title: formatMessage({ id: 'p.urc.columns.userCount' }),
            dataIndex: 'user_count',
            key: 'user_count',
            render: (count: number) => count ?? 0,
        },
        {
            title: formatMessage({ id: 'p.urc.columns.createdAt' }),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '–'),
        },
    ];

    return (
        <Permission permission={PERMISSION}>
            <ProCard title={formatMessage({ id: 'p.urc.title' })} headerBordered loading={loading}>
                <Table
                    columns={columns}
                    dataSource={channels}
                    pagination={false}
                    rowKey="id"
                    size="middle"
                />
            </ProCard>
        </Permission>
    );
};

export default PageRegistrationChannels;
